import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Download, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, download, fmtDateTime } from "../lib/api";
import { useApi, Loading, ErrorNote, PageHeader, Panel, inputCls, btn, StatusChip } from "../components/kit";

const RESULTS = [["pass", "Pass", "bg-emerald-600 text-white border-emerald-600"], ["not_yet", "Not yet", "bg-rose-600 text-white border-rose-600"], ["na", "N/A", "bg-slate-600 text-white border-slate-600"]];

export const reportPdf = (sid, date) => download(`/signoffs/${sid}/pdf`, `DCBC_walkthrough_${date}.pdf`).catch((e) => toast.error(errMsg(e)));

function StepRow({ i, item, st, onChange }) {
  return (
    <li className="rounded-xl border bg-white p-4" data-testid={`wt-step-${i}`}>
      <p className="font-medium"><span className="mr-2 text-muted-foreground">{i + 1}.</span>{item}</p>
      <div className="mt-3 flex flex-wrap gap-2" role="radiogroup" aria-label={`Result for step ${i + 1}`}>
        {RESULTS.map(([k, l, on]) => (
          <button key={k} type="button" role="radio" aria-checked={st.result === k} onClick={() => onChange({ ...st, result: k })}
            className={`tap rounded-lg border px-4 text-sm font-semibold transition-colors ${st.result === k ? on : "bg-white hover:bg-muted"}`} data-testid={`wt-step-${i}-${k}`}>{l}</button>))}
      </div>
      <textarea rows={2} className={`${inputCls} mt-3`} placeholder="Notes (optional)" aria-label={`Notes for step ${i + 1}`} value={st.note} onChange={(e) => onChange({ ...st, note: e.target.value })} data-testid={`wt-step-${i}-note`} />
    </li>
  );
}

function Attempts({ attempts }) {
  if (!attempts.length) return null;
  return (
    <Panel title="Earlier walkthroughs" testid="wt-attempts">
      <ul className="divide-y">{attempts.map((t, i) => (
        <li key={i} className="flex flex-wrap items-center justify-between gap-3 py-3">
          <span><StatusChip status={t.outcome === "pass" ? "competent" : "not_yet_competent"} /> <span className="ml-2 text-sm text-muted-foreground">Observed {t.observed_date || "—"} by {t.typed_name || t.by_name}</span>
            {t.note && <span className="mt-1 block text-sm">{t.note}</span>}</span>
          {t.signoff_id && <button className={btn.ghost} onClick={() => reportPdf(t.signoff_id, t.observed_date)} data-testid={`wt-attempt-pdf-${i}`}><Download className="h-4 w-4" /> PDF</button>}
        </li>))}</ul>
    </Panel>
  );
}

function ReportForm({ w, onDone }) {
  const [steps, setSteps] = useState(w.items.map(() => ({ result: "", note: "" })));
  const [date, setDate] = useState(w.today);
  const [note, setNote] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const all = steps.every((s) => s.result);
  const anyNotYet = steps.some((s) => s.result === "not_yet");
  const base = all && date && name.trim() && !busy;
  const canPass = base && !anyNotYet && steps.some((s) => s.result === "pass");
  const canNotYet = base && anyNotYet && note.trim();
  const send = async (outcome) => {
    setBusy(true);
    try {
      await api.post(`/assignments/${w.assignment_id}/walkthrough`, { steps, observed_date: date, note, typed_name: name, outcome });
      toast.success(outcome === "pass" ? `Walkthrough passed. It's now on ${w.user_name.split(" ")[0]}'s training record.` : "Saved as not yet. They can see what to practise.");
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-6">
      <Panel title="Observation" testid="wt-observation">
        <label htmlFor="wt-date" className="mb-1 block text-sm font-semibold">Date you observed the walkthrough</label>
        <input id="wt-date" type="date" max={w.today} className={`${inputCls} max-w-[220px]`} value={date} onChange={(e) => setDate(e.target.value)} data-testid="wt-date" />
      </Panel>
      <Panel title={`What to watch for (${w.items.length})`} testid="wt-steps">
        <p className="mb-4 text-sm text-muted-foreground">Mark each step as you watch. Use N/A only if it didn't come up on this brew.</p>
        <ol className="space-y-3">{w.items.map((it, i) => <StepRow key={i} i={i} item={it} st={steps[i]} onChange={(n) => setSteps(steps.map((x, k) => (k === i ? n : x)))} />)}</ol>
      </Panel>
      <Panel title="Result and signature" testid="wt-sign">
        <label htmlFor="wt-note" className="text-sm font-semibold">Overall comments {anyNotYet ? "(required for not yet)" : "(optional)"}</label>
        <textarea id="wt-note" rows={3} className={inputCls} value={note} onChange={(e) => setNote(e.target.value)} data-testid="wt-note" />
        <label htmlFor="wt-name" className="mt-4 block text-sm font-semibold">Type your name to sign</label>
        <input id="wt-name" className={inputCls} value={name} onChange={(e) => setName(e.target.value)} data-testid="wt-name" />
        {!all && <p className="mt-3 text-sm text-amber-800" data-testid="wt-incomplete">Give every step a result to finish the report.</p>}
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <button className={btn.primary} disabled={!canPass} onClick={() => send("pass")} data-testid="wt-pass-button"><ClipboardCheck className="h-4 w-4" /> Pass and sign off</button>
          <button className={btn.ghost} disabled={!canNotYet} onClick={() => send("not_yet")} data-testid="wt-not-yet-button">Not yet: needs more practice</button>
        </div>
      </Panel>
    </div>
  );
}

export default function WalkthroughReport() {
  const { aid } = useParams();
  const nav = useNavigate();
  const { data: w, loading, error, reload } = useApi(`/walkthroughs/${aid}`);
  if (loading && !w) return <Loading />;
  if (error) return <ErrorNote error={error} />;
  return (
    <div className="max-w-3xl space-y-6">
      <Link to="/" className="inline-flex items-center gap-1 text-sm font-semibold text-muted-foreground"><ArrowLeft className="h-4 w-4" /> Home</Link>
      <PageHeader eyebrow="Walkthrough report" title={`${w.user_name} · ${w.course_title}`} testid="wt-header" />
      <p className="-mt-4 text-sm text-muted-foreground">Version {w.version}{w.employee_id && ` · Employee ${w.employee_id}`}{w.since && ` · Ready since ${fmtDateTime(w.since)}`}</p>
      {w.awaiting ? <ReportForm w={w} onDone={() => { reload(); nav("/"); }} />
        : <p className="rounded-lg bg-muted p-4 text-sm" data-testid="wt-not-open">{w.status === "completed" ? "This walkthrough has been signed off and the course is complete." : "This person hasn't finished the lessons and quiz yet, so the walkthrough isn't open."}</p>}
      <Attempts attempts={w.attempts} />
    </div>
  );
}
